import { Show, type Component } from 'solid-js';

import type { GameStore } from '../state/store';
import {
  costText,
  gateText,
  nextRankText,
  weaponFamilyName,
  weaponMet,
  weaponRequirementText,
  type SkillRow,
} from './skills';

/**
 * Everything a tooltip says about a skill — **one renderer, two surfaces**.
 *
 * Extracted from `SkillsPanel` when the action bar needed the same card. The bar had a native
 * `title=` string built by `slotTooltip`: a name, a rank and a cast time, and none of the
 * description, cost, next-rank gain or gate. So the panel explained a skill and the bar — the place a
 * player actually looks mid-fight — did not.
 *
 * The same reasoning as `ItemTooltipBody` and `affixLine` before it, and the fault it is written to
 * prevent is the one those two were: a card rendered in two places is a card that is right in one of
 * them. Every line below reached the panel and none of it reached the bar.
 *
 * Takes a whole `SkillRow` rather than a `SkillInfo`, because half of what is worth saying is about
 * *this player's* relationship to the skill — the rank they bought, what the next one costs, whether
 * the gate is met and by how much it is missed.
 */
export const SkillTooltipBody: Component<{
  row: SkillRow;
  /**
   * The store, for the one fact that is not on the row: what is in the main hand.
   *
   * Three of the hundred skills name a weapon family, and the requirement is marked unmet against
   * what is *actually held* — so a bow skill greys the moment the bow comes off.
   */
  store: GameStore;
}> = (props) => {
  const def = () => props.row.def;
  const mainHand = () => props.store.self()?.weapon;
  // `weaponMet`, not a second copy of the rule. It already existed for the panel, and the whole point
  // of this extraction was to stop the same fact being expressed twice.
  const weaponHeld = () => weaponMet(def(), mainHand());

  return (
    <>
        <p class="skills__tip-name">{def().name}</p>
        <p class="skills__tip-meta">
          Tier {def().tier} · {def().category.replace(/_/g, ' ')} · {def().scalingType}
        </p>
        <p class="skills__tip-desc">{def().description}</p>
        <p class="skills__tip-cost">{costText(def())}</p>
        {/*
          What the next rank buys, read straight off the server's ladder rather than
          computed here — the curve shapes live in a YAML this client has no copy of.
          Absent at the top rank and for a skill that has no ladder at all, which is
          the same fourteen skills the rank button explains.
        */}
        <Show when={nextRankText(props.row) !== ''}>
          <p class="skills__tip-next" data-testid={`next-rank-${def().skillId}`}>
            Rank {props.row.rank + 1}: {nextRankText(props.row)}
          </p>
        </Show>
        <p
          class="skills__tip-gate"
          classList={{ 'skills__tip-gate--unmet': props.row.locked }}
        >
          Requires {gateText(def())}
          <Show when={props.row.locked}>
            {' '}
            — you have {props.row.missing.map((m) => `${m.have} ${m.attribute}`).join(', ')}
          </Show>
        </p>
        {/*
          The weapon requirement, on the three skills that have one. Absent for the other
          97 rather than saying "any weapon" on every row — the panel's problem is that a
          hundred skills is already more than anyone reads, and a line marking the
          absence of a rule is a hundred lines to flag three exceptions.

          Marked unmet against what is actually in the main hand, so a bow skill greys
          the moment the bow comes off. **Advisory**: the server re-checks it on every
          cast and its answer is the one that decides.
        */}
        <Show when={weaponRequirementText(def()) !== ''}>
          <p
            class="skills__tip-gate"
            classList={{ 'skills__tip-gate--unmet': !weaponHeld() }}
            data-testid={`weapon-req-${def().skillId}`}
          >
            {weaponRequirementText(def())}
            <Show when={!weaponHeld()}>
              {' '}
              — you are holding {mainHand() ? weaponFamilyName(mainHand()!) : 'nothing'}
            </Show>
          </p>
        </Show>
        <Show when={def().scalingType === 'PASSIVE'}>
          <p class="skills__tip-note">Always on once learned — there is nothing to cast.</p>
        </Show>
    </>
  );
};
